import { Grid, Paper, Toolbar, Tooltip, Typography } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import StatusPage from '../../components/status-page';
import { VersionContext } from '../../contexts/version';
import * as helpers from '../../helpers';
import Link from '../../link/contrast';

const infoSiteURI = 'https://www.blackbird.online';

const useStyles = makeStyles((theme: Theme) => ({
  wrapLinks: {
    whiteSpace: 'normal',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  verticalPadding: {
    padding: theme.spacing(2),
  },
  bar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    position: 'relative',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      padding: '1em',
    },
    boxShadow: 'none',
  },
  contentJustification: {
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  stagingStriped: {
    backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.primary.main}, ${theme.palette.primary.main} 10px, rgba(255, 255, 255, 0.075) 10px, rgba(255, 255, 255, 0.075) 20px)`,
  },
  version: {
    color: grey['500'],
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      textAlign: 'center',
    },
  },
  plainLink: {
    color: '#FFB200',
    textDecoration: 'none',
  },
}));

const joinWithPipe = helpers.joinWith(' | ');

interface Properties {
  helpLink?: string;
  gettingStartedLink?: string;
  whatsNewLink?: string;
  hideSupportLink?: boolean;
}

const Footer: FC<Properties> = ({ helpLink, gettingStartedLink, whatsNewLink, hideSupportLink = false }) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const { commit, version } = useContext(VersionContext);

  const year = new Date().getFullYear();

  const supportParts: JSX.Element[] = [];

  supportParts.push(<StatusPage />);

  if (gettingStartedLink) {
    supportParts.push(<Link href={`${gettingStartedLink}`}>{t('shared:gettingStarted', 'Getting started')}</Link>);
  }

  if (helpLink) {
    supportParts.push(<Link href={`${helpLink}`}>{t('shared:help', 'Help')}</Link>);
  }

  if (whatsNewLink) {
    supportParts.push(
      <Link href={`${whatsNewLink}`} target="_blank" rel="noopener noreferrer">
        {t('shared:whatsNew', "What's new")}
      </Link>,
    );
  }

  supportParts.push(<Link href={`${infoSiteURI}/about`}>{t('shared:about', 'About')}</Link>);

  supportParts.push(<Link href={`${infoSiteURI}/contact`}>{t('shared:contact', 'Contact')}</Link>);

  if (!hideSupportLink) {
    supportParts.push(<Link href={`${infoSiteURI}/support`}>{t('shared:support', 'Support')}</Link>);
  }

  const isStaging = !!(version && !/^v\d+\.\d+\.\d+$/.test(version));

  // FIXME: Find out where to set position="static" on the new MUI paper component.
  return (
    <Paper
      square
      component="footer"
      elevation={4}
      className={clsx(classes.bar, { [classes.stagingStriped]: isStaging })}
    >
      <Toolbar className={classes.verticalPadding}>
        <Grid container spacing={0} className={classes.contentJustification}>
          <Grid item sm={6}>
            <Typography color="inherit" variant="subtitle1">
              {`© ${year} Blackbird ApS. All rights reserved. `}
              {commit && version ? (
                <Tooltip placement="top" title={`Build ${commit}`}>
                  <span className={`${classes.version} hideInPercy`}>{`Version ${version}`}</span>
                </Tooltip>
              ) : null}
            </Typography>
          </Grid>
          <Grid item sm={6} className="hideInPercy">
            <Typography className={classes.wrapLinks} align="right" color="inherit" variant="subtitle1">
              {supportParts.reduce(joinWithPipe, [])}
            </Typography>
          </Grid>
        </Grid>
      </Toolbar>
    </Paper>
  );
};

export default Footer;
